import React, { useState, useMemo } from 'react';
import { MdClose, MdSearch, MdCheck, MdApps } from 'react-icons/md';
import {
  ALL_ASSIST_ICONS,
  ASSIST_ICON_CATEGORIES,
} from '../../../Utils/AssistIcons';

export default function AssistIconPickerModal({
  isOpen,
  onClose,
  selectedIcon,
  onSelectIcon,
  primaryColor = '#1c4fa6',
}) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [tempSelected, setTempSelected] = useState(selectedIcon);

  // Sync tempSelected when opening modal
  React.useEffect(() => {
    if (isOpen) {
      setTempSelected(selectedIcon);
      setSearch('');
      setActiveCategory('All');
    }
  }, [isOpen, selectedIcon]);

  const filteredIcons = useMemo(() => {
    const term = search.trim().toLowerCase();
    return ALL_ASSIST_ICONS.filter((item) => {
      const matchesCategory =
        activeCategory === 'All' || item.category === activeCategory;
      const matchesSearch =
        !term ||
        item.label.toLowerCase().includes(term) ||
        item.value.toLowerCase().includes(term) ||
        item.category.toLowerCase().includes(term);
      return matchesCategory && matchesSearch;
    });
  }, [search, activeCategory]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onSelectIcon(tempSelected);
    onClose();
  };

  const handleCardClick = (value) => {
    setTempSelected(value);
  };

  const handleCardDoubleClick = (value) => {
    onSelectIcon(value);
    onClose();
  };

  return (
    <div
      className="hc-ai-modal-backdrop d-flex align-items-center justify-content-center p-3"
      onClick={onClose}
      style={{ zIndex: 1060 }}
    >
      <div
        className="hc-ai-icon-modal d-flex flex-column overflow-hidden"
        style={{ '--widget-color': primaryColor }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-3 border-bottom d-flex align-items-center justify-content-between bg-white">
          <div className="d-flex align-items-center gap-2">
            <span
              className="d-inline-flex align-items-center justify-content-center text-white rounded-3 flex-shrink-0"
              style={{ width: 36, height: 36, backgroundColor: primaryColor }}
            >
              <MdApps size={20} />
            </span>
            <div>
              <h5 className="fw-bold mb-0 hc-text-primary" style={{ fontSize: '1.1rem' }}>
                Select Widget Icon
              </h5>
              <small className="text-muted">
                Choose an icon representing your AI assistant
              </small>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-light rounded-circle p-1 d-inline-flex align-items-center justify-content-center"
            onClick={onClose}
            aria-label="Close"
            style={{ width: 32, height: 32 }}
          >
            <MdClose size={18} />
          </button>
        </div>

        {/* Search & Categories */}
        <div className="p-3 border-bottom bg-light">
          <div className="position-relative mb-2">
            <span
              className="position-absolute d-inline-flex text-muted"
              style={{ top: '50%', left: 12, transform: 'translateY(-50%)' }}
            >
              <MdSearch size={19} />
            </span>
            <input
              type="text"
              className="form-control ps-5 rounded-3"
              placeholder="Search icons by name or role..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            {search ? (
              <button
                type="button"
                className="position-absolute end-0 top-50 translate-middle-y btn btn-link text-muted pe-3 text-decoration-none"
                onClick={() => setSearch('')}
                aria-label="Clear search"
              >
                <MdClose size={16} />
              </button>
            ) : null}
          </div>

          <div className="d-flex flex-wrap gap-1 mt-2">
            {ASSIST_ICON_CATEGORIES.map((cat) => {
              const active = activeCategory === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  className={`hc-ai-icon-pill ${active ? 'is-active' : ''}`}
                  style={active ? { backgroundColor: primaryColor, borderColor: primaryColor } : undefined}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>

        {/* Icon Grid */}
        <div className="hc-ai-icon-modal-body p-3 flex-fill bg-white">
          {filteredIcons.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <MdSearch size={36} className="mb-2 opacity-50" />
              <p className="mb-0 fw-semibold">No icons found matching "{search}"</p>
              <small>Try searching with another keyword</small>
            </div>
          ) : (
            <div className="hc-ai-icon-grid">
              {filteredIcons.map((option) => {
                const isSelected = tempSelected === option.value;
                const IconComponent = option.Icon;
                return (
                  <button
                    key={option.value}
                    type="button"
                    title={`${option.label} (${option.category})`}
                    onClick={() => handleCardClick(option.value)}
                    onDoubleClick={() => handleCardDoubleClick(option.value)}
                    className={`hc-ai-icon-card ${isSelected ? 'is-active' : ''}`}
                    style={
                      isSelected
                        ? {
                            borderColor: primaryColor,
                            backgroundColor: 'rgba(28, 79, 166, 0.08)',
                            boxShadow: `0 0 0 1.5px ${primaryColor}`,
                          }
                        : undefined
                    }
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center mb-1 position-relative"
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        backgroundColor: isSelected ? primaryColor : 'var(--hc-tint-2)',
                        color: isSelected ? '#ffffff' : 'var(--hc-text)',
                        transition: 'all 0.18s ease',
                      }}
                    >
                      <IconComponent size={20} />
                      {isSelected ? (
                        <span
                          className="position-absolute rounded-circle d-flex align-items-center justify-content-center text-white"
                          style={{
                            top: -4,
                            right: -4,
                            width: 15,
                            height: 15,
                            backgroundColor: primaryColor,
                            border: '1.5px solid #fff',
                            fontSize: 9,
                          }}
                        >
                          <MdCheck size={10} />
                        </span>
                      ) : null}
                    </span>
                    <span className="hc-ai-icon-card-label" style={isSelected ? { color: primaryColor } : undefined}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-top bg-light d-flex align-items-center justify-content-between">
          <div className="text-muted small">
            {filteredIcons.length} icon{filteredIcons.length === 1 ? '' : 's'} available
          </div>
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary px-3 rounded-3"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sm text-white px-3 rounded-3 fw-bold"
              style={{ backgroundColor: primaryColor }}
              onClick={handleConfirm}
            >
              Select Icon
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
